import BaseApi from '@/api/Base/BaseApi'
import {
  AutoDeployEndpoint,
  AutoDeployEndpointHistoryListResponse,
  AutoDeployEndpointResetSecretHistory,
  AutoDeploySettings,
  CreateAutoDeployEndpointPayload,
  CreateAutoDeploySettingsPayload,
  GetAutoDeployEndpointHistoryListPayload,
  HasAutoDeploySettingsResponse,
  ReplaceAutoDeploySettingsPayload,
  UpdateAutoDeployEndpointPayload,
  UpdateAutoDeploySettingsPayload
} from '@/api/AutoDeploy/types'
import { ProjectNodeType } from '@/api/Project/types'
import { ModelValueType } from '@/pages/const'

const ROOT = '/auto-deploy'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class AutoDeployApi extends BaseApi {
  createAutoDeployEndpoint(payload: CreateAutoDeployEndpointPayload) {
    return this.agent.post<AutoDeployEndpoint>(`${ROOT}/endpoint`, payload)
  }

  getAutoDeployEndpoint(autoDeployEndpointId: string) {
    return this.agent.get<AutoDeployEndpoint>(`${ROOT}/endpoint/${autoDeployEndpointId}`)
  }

  updateAutoDeployEndpoint(payload: UpdateAutoDeployEndpointPayload) {
    const { autoDeployEndpointId, ...rest } = payload
    return this.agent.put<AutoDeployEndpoint>(`${ROOT}/endpoint/${autoDeployEndpointId}`, { ...rest })
  }

  deleteAutoDeployEndpoint(autoDeployEndpointId: string) {
    return this.agent.delete<AutoDeployEndpoint>(`${ROOT}/endpoint/${autoDeployEndpointId}`)
  }

  getWorkspaceAutoDeployEndpointList(workspaceId: string) {
    return this.agent.get<AutoDeployEndpoint[]>(`${ROOT}/workspace/${workspaceId}/endpoint/list`)
  }

  toggleActivateAutoDeployEndpoint(autoDeployEndpointId: string) {
    return this.agent.post<AutoDeployEndpoint>(`${ROOT}/endpoint/${autoDeployEndpointId}/toggle-activate`)
  }

  resetAutoDeployEndpointSecret(autoDeployEndpointId: string) {
    return this.agent.post<AutoDeployEndpoint>(`${ROOT}/endpoint/${autoDeployEndpointId}/reset-secret`)
  }

  exportAutoDeployEndpointConfig(autoDeployEndpointId: string) {
    return this.agent.get<any>(`${ROOT}/endpoint/${autoDeployEndpointId}/export-config`, {
      responseType: 'blob',
    })
  }

  getAutoDeployEndpointHistories(autoDeployEndpointId: string, options?: GetAutoDeployEndpointHistoryListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? { ...defaultPaginationOptions }
    return this.agent.get<AutoDeployEndpointHistoryListResponse>(`${ROOT}/endpoint/${autoDeployEndpointId}/history-list`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getAutoDeployEndpointResetSecretHistories(autoDeployEndpointId: string) {
    return this.agent.get<AutoDeployEndpointResetSecretHistory[]>(`${ROOT}/endpoint/${autoDeployEndpointId}/reset-secret-histories`)
  }

  getSelectableAutoDeployEndpointList(workspaceId: string, modelType: ModelValueType) {
    return this.agent.get<AutoDeployEndpoint[]>(`${ROOT}/endpoint/selectable-list`, {
      params: {
        workspaceId,
        modelType,
      },
    })
  }

  getNodeAutoDeploySettings(nodeId: string, nodeType: ProjectNodeType) {
    return this.agent.get<{ exists: boolean, autoDeploySettings: AutoDeploySettings | null }>(`${ROOT}/node/${nodeId}/settings`, {
      params: {
        nodeType,
      },
    })
  }

  createAutoDeploySettings(payload: CreateAutoDeploySettingsPayload) {
    return this.agent.post<AutoDeploySettings>(`${ROOT}/settings`, payload)
  }

  updateAutoDeploySettings(autoDeploySettingsId: string, payload: UpdateAutoDeploySettingsPayload) {
    return this.agent.put<AutoDeploySettings>(`${ROOT}/settings/${autoDeploySettingsId}`, payload)
  }

  hasAutoDeploySettings(autoDeployEndpointId: string) {
    return this.agent.get<HasAutoDeploySettingsResponse>(`${ROOT}/endpoint/${autoDeployEndpointId}/has-settings`)
  }

  replaceAutoDeploySettings(autoDeploySettingsId: string, payload: ReplaceAutoDeploySettingsPayload) {
    return this.agent.put<AutoDeploySettings>(`${ROOT}/settings/${autoDeploySettingsId}/replace`, payload)
  }

  cancelAutoDeploy(autoDeploySettingsId: string) {
    return this.agent.post(`${ROOT}/settings/${autoDeploySettingsId}/cancel`)
  }
}
